import { useState } from "react";
import { Link } from "react-router-dom";
import { usePortalContext } from "../context/PortalContext";
import type { Portal } from "../context/PortalContext";
import styles from "./PortalsPanel.module.css";

function statusEmoji(status: Portal["status"]): string {
  if (status === "connected") return "✅";
  if (status === "needs_review") return "⚠️";
  return "❌";
}

function statusLabel(status: Portal["status"]): string {
  if (status === "connected") return "Connected";
  if (status === "needs_review") return "Needs review";
  return "Disconnected";
}

export default function PortalsPanel() {
  const { portals, renamePortal } = usePortalContext();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  function startEdit(portal: Portal) {
    setEditingId(portal.id);
    setEditValue(portal.nickname ?? "");
  }

  function commitEdit(id: string) {
    renamePortal(id, editValue.trim());
    setEditingId(null);
  }

  return (
    <div className={styles["portals-panel"]}>
      <div className={styles["portals-header"]}>
        <span className="section-label">CONNECTED PORTALS</span>
        <Link to="/portals" className={styles["manage-link"]}>
          Manage →
        </Link>
      </div>

      {portals.length > 0 && (
        <ul className={styles["portals-list"]}>
          {portals.map((portal) => (
            <li key={portal.id} className={styles["portal-row"]}>
              <span
                className={styles["portal-status-emoji"]}
                title={statusLabel(portal.status)}
              >
                {statusEmoji(portal.status)}
              </span>
              <span className={styles["portal-name-area"]}>
                {editingId === portal.id ? (
                  <input
                    className={styles["portal-name-input"]}
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={() => commitEdit(portal.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitEdit(portal.id);
                      if (e.key === "Escape") setEditingId(null);
                    }}
                    autoFocus
                    placeholder={portal.id}
                  />
                ) : (
                  <>
                    <span className={styles["portal-name"]}>
                      {portal.nickname ?? portal.name}
                    </span>
                    {(portal.nickname || portal.name !== portal.id) && (
                      <span className={styles["portal-id-secondary"]}>
                        ({portal.id})
                      </span>
                    )}
                  </>
                )}
              </span>
              <button
                className={styles["portal-rename-btn"]}
                onClick={() => startEdit(portal)}
                title="Rename portal"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
              <span className={styles["portal-status-label"]}>
                {statusLabel(portal.status)}
              </span>
            </li>
          ))}
        </ul>
      )}

      <a
        href="/api/portals/oauth/init"
        className={styles["connect-portal-cta"]}
      >
        + Connect a portal
      </a>
    </div>
  );
}
